import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { Booking } from '../../../bookings/entities/Booking.js';
import type { ClassBooking } from '../../../bookings/entities/ClassBooking.js';
import type { AGEingruppierung } from '../../../contracts/entities/vertrag-sektionen/vertrag-eingruppierung/AGEingruppierung.js';
import type { Person } from '../../entities/person.js';

const subPersonSelectionSet: BaseQLSelectionSet<Person> = {
  _id: 1,
  email: 1,
  kreditorenNr: 1,
  debitorennummer: 1,
  legacyDebitorNumbers: {
    debitorNumber: 1,
    scope: 1,
  },
  nameAbbreviation: 1,
  profilDaten: {
    stammdaten: {
      titel: 1,
      geburtsDatum: 1,
      telefonNr: 1,
      vorname: 1,
      nachname: 1,
    },
    adresse: {
      streetAndNumber: 1,
      zip: 1,
      city: 1,
      country: 1,
      auslandsPlz: 1,
      attentionLine: 1,
      district: 1,
    },
    bankInfos: {
      iban: 1,
      kontoinhaber: 1,
      nichtSepa: 1,
      nichtSepaLand: 1,
      nichtSepaKreditinstitut: 1,
      nichtSepaOrt: 1,
      nichtSepaSWIFT: 1,
      nichtSepaIBAN: 1,
      nichtSepaLaenderschluessel: 1,
      ibanWrong: 1,
    },
  },
  sepaMandats: {
    _id: 1,
    _class: 1,
    name: 1,
    kuerzel: 1,
    start: 1,
    end: 1,
    rhythm: 1,
    acceptedAt: 1,
    acceptedPersonName: 1,
    iban: 1,
    amountInCents: 1,
    active: 1,
    mandateReference: 1,
    billedOn: 1,
    accepted: 1,
    billingLocked: 1,
  },
  saldo: 1,
};

const contractEingruppierungSelectionSet: BaseQLSelectionSet<AGEingruppierung> = {
  agVertragsInfos: {
    startDate: 1,
    endDate: 1,
    taetigkeit: 1,
    taetigkeitsschluessel: 1,
    kostenstelleProzentsaetze: {
      kostenstelleID: 1,
      kostenstelle: {
        _id: 1,
        name: 1,
        kostenstelleSuffix: 1,
        school: {
          _id: 1,
          name: 1,
          kostenstellePrefix: 1,
        },
      },
      prozentsatz: 1,
    },
    zeitarbeitsverhaeltnis: 1,
    stellenbezeichnung: 1,
    endeProbezeit: 1,
    sachkontenzuordnung: 1,
  },
  agLohnGehaltszahlungen: {
    entgeltart: 1,
    tarifgruppe: 1,
    fiktivBeginnStufe1: 1,
    manuelleErfahrungsstufe: 1,
    monatlicheZahlungen: {
      _id: 1,
      lohnart: 1,
      lohnartNumber: 1,
      betrag: 1,
      monat: 1,
      jahr: 1,
    },
  },
  agArbeitszeit: {
    maxDeputat: 1,
    zaehler: 1,
    stunden: 1,
    ferienEinarbeiten: 1,
  },
  paidWorkTime: 1,
  totalWorkTime: 1,
};

const bookingSelectionSet: BaseQLSelectionSet<Booking | ClassBooking> = {
  _id: 1,
  _class: 1,
  startDate: 1,
  endDate: 1,
  groupIDs: 1,
  grade: 1,
  classIdentifier: 1,
  createdAt: 1,
  billingPosition: { priceInCents: 1 },
  groups: {
    _id: 1,
    _class: 1,
    name: 1,
    kostenstellePrefix: 1,
    kostenstelleSuffix: 1,
    abwesenheitsemail: 1,
    billingPosition: {
      kuerzel: 1,
      name: 1,
      buchungskonto: 1,
      priceInCents: 1,
      rhythm: 1,
      kostenstelleID: 1,
    },
    parents: {
      _id: 1,
      _class: 1,
      name: 1,
      // @ts-expect-error on inherited properties
      kostenstellePrefix: 1,
      kostenstelleSuffix: 1,
      abwesenheitsemail: 1,
    },
  },
};

export const fullDataviewWhitelist: BaseQLSelectionSet<Person> = {
  _class: 1,
  email: 1,
  debitorennummer: 1,
  externalUniqueID: 1,
  legacyDebitorNumbers: {
    debitorNumber: 1,
    scope: 1,
  },
  nameAbbreviation: 1,
  lastLogin: 1,
  saldo: 1,
  lakHoursUntilLastSchoolYear: 1,
  microsoftUserData: {
    microsoftID: 1,
    shareWithMicrosoft: 1,
    manuallyChangedName: 1,
    userPrincipalName: 1,
    mail: 1,
    otherMails: 1,
  },
  roles: {
    name: 1,
    contexts: 1,
  },
  profilDaten: {
    stammdaten: {
      titel: 1,
      geburtsDatum: 1,
      telefonNr: 1,
      vorname: 1,
      nachname: 1,
      namenszusatz: 1,
      vorsatzWort: 1,
      geschlecht: 1,
      geburtsLand: 1,
      geburtsLandFullName: 1,
      geburtsOrt: 1,
      staatsangehoerigkeit: 1,
      staatsangehoerigkeit2: 1,
      staatsangehoerigkeitFullName: 1,
      staatsangehoerigkeit2FullName: 1,
      konfession: 1,
      religion: 1,
    },
    adresse: {
      streetAndNumber: 1,
      zip: 1,
      city: 1,
      country: 1,
      auslandsPlz: 1,
      attentionLine: 1,
      district: 1,
    },
    bankInfos: {
      iban: 1,
      kontoinhaber: 1,
      nichtSepa: 1,
      nichtSepaLand: 1,
      nichtSepaKreditinstitut: 1,
      nichtSepaOrt: 1,
      nichtSepaSWIFT: 1,
      nichtSepaIBAN: 1,
      nichtSepaLaenderschluessel: 1,
      ibanWrong: 1,
    },
  },
  schuelerDaten: {
    heinNummer: 1,
    eingeladeneSorgeberechtigte: 1,
    eingeladeneSpender: 1,
    wantedKlassenstufe: 1,
    bewerbungsDate: 1,
    zuzugsDate: 1,
    ablehnungGrund: 1,
    participationReligion: 1,
    hasSiblingsAtMartinusSchools: 1,
    lastGradeGerman: 1,
    currentKlassenstufeAtBewerbungsDate: 1,
    schulwechselMotivation: 1,
    motivation: 1,
    languageAtHomeIsGerman: 1,
    lastGradeMath: 1,
    lastGradeSachunterricht: 1,
    interestInNativeLanguageCourses: 1,
    interestInWholeDaySchooling: 1,
    schoolyearsAfterElementary: 1,
    internalNotes: 1,
    susBewerberStatus: 1,
  },
  mitarbeiterDaten: {
    personalNr: 1,
    lodasPersonalNr: 1,
    altePersonalNr: 1,
    jobTicketKenntnis: 1,
    jobTicketBestaetigt: 1,
    jobTicketDatenweitergabe: 1,
    deutschlandTicketBestellt: 1,
    jobTicketBestellDatum: 1,
    jobTicketKuendigungsDatum: 1,
    jobTicketExportiertAm: 1,
    anInfos: {
      geburtsName: 1,
      familienstand: 1,
      hatKinder: 1,
      anzahlKinder: 1,
      hatAufenthaltserlaubnis: 1,
      hatArbeitserlaubnis: 1,
      arbeitserlaubnisID: 1,
      gueltigAb: 1,
      ausstellungsDatumArbeitserlaubnis: 1,
      gueltigBis: 1,
      aufenthaltsgenehmigungID: 1,
      kinder: {
        vorname: 1,
        nachname: 1,
        geburtsdatum: 1,
      },
    },
    anDokumente: {
      fuehrungszeugnisAusstellung: 1,
      vorsorgeaufwendungenID: 1,
      vermoegensLeistungenID: 1,
      gesundheitszeugnisID: 1,
      fuehrungszeugnisID: 1,
      masernimpfungID: 1,
      lebenslaufID: 1,
      personalbogenID: 1,
      erklaerungStrafverfahrenID: 1,
      infektionsschutzID: 1,
      datenschutzID: 1,
      selbstversplichtungserklaerungID: 1,
      zeugnisseIDs: 1,
      sonstigeIDs: 1,
      arbeitsvertragIDs: 1,
      nachtragIDs: 1,
      studienbescheinigungsIDs: 1,
      beschaeftigungsgenehmigungIDs: 1,
    },
    anSteuerInfos: {
      verfuegbarkeit: 1,
      steuerIdentNr: 1,
      steuerklasse: 1,
      steuerklassePartner: 1,
      factor: 1,
      kinderFreibetrag: 1,

      konfessionPartner: 1,
      steuerfreibetraege: {
        jahresFreibetrag: 1,
        jahresHinzurechnungsbetrag: 1,
        monatsFreibetrag: 1,
        monatsHinzurechnungsbetrag: 1,
      },
    },
    anSozialVersicherung: {
      versicherungsStatus: 1,
      rentenVersicherungsNr: 1,
      krankenkassenID: 1,
      krankenkassen: {
        rahmenNr: 1,
        krankenkasse: 1,
        bezeichnung: 1,
        ort: 1,
        betriebsnummer: 1,
      },
    },
    anAusbildung: {
      hoechsterSchulAbschluss: 1,
      hoechsterBerufsAbschluss: 1,
    },
    anPrivatVersicherung: {
      istPrivatVersichert: 1,
      letzteGesKrankenkasseID: 1,
      anspruchKrankentageGeld: 1,
      krankenBeitrag: 1,
      pflegenBeitrag: 1,
      arbeitgeberzuschussID: 1,
      isPrivatPflegeversichert: 1,
      beitragsanteilBasisKrankenversicherung: 1,
    },
    anGeringfuegigeBeschaeftigung: {
      geringfuegigeBeschaeftigungGewuenscht: 1,
      befreiungRentenversicherungspflichtGewuenscht: 1,
      pauschalbesteuerungGeringfuegig: 1,
      pauschalbesteuerungGeringfuegigSonstige: 1,
      beschaeftigungslos: 1,
      beschaefitgungslosLeistungsbezug: 1,
      geringfuegigKrankenkasseBetriebsnummer: 1,
      krankenversicherung: 1,
      artKrankenversicherung: 1,
      beschaftigung: 1,
      beschaeftigungen: {
        beschaftigungsbeginn: 1,
        arbeitgeberMitAdresse: 1,
        entlohnungsArt: 1,
        mitEigenanteilZurRv: 1,
        lohn: 1,
      },

      arbeit: 1,
      arbeits: {
        beginBeschaftigungMeldung: 1,
        endeBeschaftigungMeldung: 1,
        monatlichesArbeitsentgeltGroesserAls520Euro: 1,
        tatsachlicheArbeitstageInDiesemZeitraum: 1,
        arbeitgeberMitAdresse: 1,
      },

      auslandsbeschaeftigung: 1,
      auslandbeschaeftigung: {
        beschaftigungsBeginn: 1,
        beschaftigungsEnde: 1,
        arbeitgeberMitAdresse: 1,
      },
      beantrageBefreiungRentenversicherung: 1,
    },
    anSchwerbehinderung: {
      istVorhanden: 1,
      grad: 1,
    },
    anEinverstaendnisErklaerungen: {
      selbstverpflichtung: 1,
      datenschutz: 1,
      infektionsschutz: 1,
      studentSozialversicherungspflicht: 1,
      mitarbeiterInformation: 1,
    },
    anBeschaeftigung: {
      employeeType: 1,
      personenGruppe: 1,
      abrechnungsgruppe: 1,
    },
  },
  sorgeberechtigteEltern: subPersonSelectionSet,
  sorgeberechtigteKinder: subPersonSelectionSet,
  spender: subPersonSelectionSet,
  spendenKinder: subPersonSelectionSet,
  // the person view stores only names and birth dates of the siblings - anything more would come back empty
  geschwister: { _id: 1, profilDaten: { stammdaten: { vorname: 1, nachname: 1, geburtsDatum: 1 } } },
  buchungen: bookingSelectionSet,
  activeBuchungen: bookingSelectionSet,
  bookingsOfChildren: bookingSelectionSet,
  activeBuchungenOfChildren: bookingSelectionSet,
  activeContract: {
    _id: 1,
    createdAt: 1,
    justification: 1,
    active: 1,
    appliedDate: 1,
    supplements: {
      _id: 1,
      nachtragsNummer: 1,
      subNachtragsnummer: 1,
      createdAt: 1,
      justification: 1,
    },
    hauptarbeitgeber: 1,
    erfahrungsstufeAg1: 1,
    erfahrungsstufeAg2: 1,
    agInfos: {
      unterzeichnungsdatum: 1,
      einstellungsdatum: 1,
      gekuendigtZum: 1,
      alleDokumenteVorgelegt: 1,
      urlaubsTage: 1,
      anstellungsform: 1,
      pauschalbesteuerungMiniJob: 1,
      dauerProbezeit: 1,
      besonderePersonengruppe: 1,
      nebenabrede: 1,
      isGestellungsContract: 1,
      workingStudentChecked: 1,
    },
    agSozialVersicherung: {
      kvAGZuschuss: 1,
      pvAGZuschuss: 1,
      pvGesamtbeitrag: 1,
      beschaeftigungsart: 1,
      altfallGleiter: 1,
      rentner: 1,
      beitragsgruppenschluesselKV: 1,
      beitragsgruppenschluesselRV: 1,
      beitragsgruppenschluesselAV: 1,
      beitragsgruppenschluesselPV: 1,
      applyMidijob: 1,
      privatVersichert: 1,
      krankentagegeld: 1,
      mehrfachbeschaeftigung: 1,
      leiharbeitsverhaeltnis: 1,
      ergaenzungSV: 1,
      geringfuegigeBeschaeftigung: 1,
    },
    agEingruppierung1: contractEingruppierungSelectionSet,
    agEingruppierung2: contractEingruppierungSelectionSet,
  },
  sepaMandats: {
    _id: 1,
    _class: 1,
    name: 1,
    kuerzel: 1,
    start: 1,
    end: 1,
    rhythm: 1,
    acceptedAt: 1,
    acceptedPersonName: 1,
    iban: 1,
    amountInCents: 1,
    active: 1,
    mandateReference: 1,
    billedOn: 1,
    accepted: 1,
    billingLocked: 1,
  },
};

export const fullDataviewWhitelistFilter: BaseQLSelectionSet<Person> = {
  _class: 1,
  email: 1,
  contractApplyDate: 1,
  debitorennummer: 1,
  externalUniqueID: 1,
  lastLogin: 1,
  legacyDebitorNumbers: {
    debitorNumber: 1,
    scope: 1,
  },
  nameAbbreviation: 1,
  saldo: 1,
  lakHoursUntilLastSchoolYear: 1,
  microsoftUserData: {
    microsoftID: 1,
    shareWithMicrosoft: 1,
    manuallyChangedName: 1,
    userPrincipalName: 1,
    mail: 1,
    otherMails: 1,
  },
  sepaMandats: {
    _id: 1,
    _class: 1,
    name: 1,
    kuerzel: 1,
    start: 1,
    end: 1,
    rhythm: 1,
    acceptedAt: 1,
    acceptedPersonName: 1,
    iban: 1,
    amountInCents: 1,
    active: 1,
    mandateReference: 1,
    billedOn: 1,
    accepted: 1,
    billingLocked: 1,
  },
  roles: {
    name: 1,
    contexts: 1,
  },
  profilDaten: {
    stammdaten: {
      titel: 1,
      geburtsDatum: 1,
      telefonNr: 1,
      vorname: 1,
      nachname: 1,
      namenszusatz: 1,
      vorsatzWort: 1,
      geschlecht: 1,
      geburtsLand: 1,
      geburtsOrt: 1,
      staatsangehoerigkeit: 1,
      staatsangehoerigkeit2: 1,
      konfession: 1,
      religion: 1,
    },
    adresse: {
      streetAndNumber: 1,
      zip: 1,
      city: 1,
      country: 1,
      auslandsPlz: 1,
      attentionLine: 1,
      district: 1,
    },
    bankInfos: {
      iban: 1,
      kontoinhaber: 1,
      nichtSepa: 1,
      nichtSepaLand: 1,
      nichtSepaKreditinstitut: 1,
      nichtSepaOrt: 1,
      nichtSepaSWIFT: 1,
      nichtSepaIBAN: 1,
      nichtSepaLaenderschluessel: 1,
      ibanWrong: 1,
    },
  },
  schuelerDaten: {
    heinNummer: 1,
    eingeladeneSorgeberechtigte: 1,
    eingeladeneSpender: 1,
    wantedKlassenstufe: 1,
    bewerbungsDate: 1,
    zuzugsDate: 1,
    ablehnungGrund: 1,
    participationReligion: 1,
    hasSiblingsAtMartinusSchools: 1,
    lastGradeGerman: 1,
    currentKlassenstufeAtBewerbungsDate: 1,
    schulwechselMotivation: 1,
    motivation: 1,
    languageAtHomeIsGerman: 1,
    lastGradeMath: 1,
    lastGradeSachunterricht: 1,
    interestInNativeLanguageCourses: 1,
    interestInWholeDaySchooling: 1,
    schoolyearsAfterElementary: 1,
    internalNotes: 1,
    susBewerberStatus: 1,
  },
  mitarbeiterDaten: {
    personalNr: 1,
    lodasPersonalNr: 1,
    altePersonalNr: 1,
    jobTicketKenntnis: 1,
    jobTicketBestaetigt: 1,
    jobTicketDatenweitergabe: 1,
    deutschlandTicketBestellt: 1,
    jobTicketBestellDatum: 1,
    jobTicketKuendigungsDatum: 1,
    jobTicketExportiertAm: 1,
    anInfos: {
      geburtsName: 1,
      familienstand: 1,
      hatKinder: 1,
      anzahlKinder: 1,
      hatAufenthaltserlaubnis: 1,
      hatArbeitserlaubnis: 1,
      arbeitserlaubnisID: 1,
      gueltigAb: 1,
      ausstellungsDatumArbeitserlaubnis: 1,
      gueltigBis: 1,
      aufenthaltsgenehmigungID: 1,
      kinder: {
        vorname: 1,
        nachname: 1,
        geburtsdatum: 1,
      },
    },
    anSteuerInfos: {
      verfuegbarkeit: 1,
      steuerIdentNr: 1,
      steuerklasse: 1,
      steuerklassePartner: 1,
      factor: 1,
      kinderFreibetrag: 1,

      konfessionPartner: 1,
      steuerfreibetraege: {
        jahresFreibetrag: 1,
        jahresHinzurechnungsbetrag: 1,
        monatsFreibetrag: 1,
        monatsHinzurechnungsbetrag: 1,
      },
    },
    anSozialVersicherung: {
      versicherungsStatus: 1,
      rentenVersicherungsNr: 1,
      krankenkassenID: 1,
      krankenkassen: {
        rahmenNr: 1,
        krankenkasse: 1,
        bezeichnung: 1,
        ort: 1,
        betriebsnummer: 1,
      },
    },
    anAusbildung: {
      hoechsterSchulAbschluss: 1,
      hoechsterBerufsAbschluss: 1,
    },
    anPrivatVersicherung: {
      istPrivatVersichert: 1,
      letzteGesKrankenkasseID: 1,
      anspruchKrankentageGeld: 1,
      krankenBeitrag: 1,
      pflegenBeitrag: 1,
      arbeitgeberzuschussID: 1,
      isPrivatPflegeversichert: 1,
      beitragsanteilBasisKrankenversicherung: 1,
    },
    anGeringfuegigeBeschaeftigung: {
      geringfuegigeBeschaeftigungGewuenscht: 1,
      befreiungRentenversicherungspflichtGewuenscht: 1,
      pauschalbesteuerungGeringfuegig: 1,
      pauschalbesteuerungGeringfuegigSonstige: 1,
      beschaeftigungslos: 1,
      beschaefitgungslosLeistungsbezug: 1,
      geringfuegigKrankenkasseBetriebsnummer: 1,
      krankenversicherung: 1,
      artKrankenversicherung: 1,
      beschaftigung: 1,
      beschaeftigungen: {
        beschaftigungsbeginn: 1,
        arbeitgeberMitAdresse: 1,
        entlohnungsArt: 1,
        mitEigenanteilZurRv: 1,
        lohn: 1,
      },

      arbeit: 1,
      arbeits: {
        beginBeschaftigungMeldung: 1,
        endeBeschaftigungMeldung: 1,
        monatlichesArbeitsentgeltGroesserAls520Euro: 1,
        tatsachlicheArbeitstageInDiesemZeitraum: 1,
        arbeitgeberMitAdresse: 1,
      },

      auslandsbeschaeftigung: 1,
      auslandbeschaeftigung: {
        beschaftigungsBeginn: 1,
        beschaftigungsEnde: 1,
        arbeitgeberMitAdresse: 1,
      },
      beantrageBefreiungRentenversicherung: 1,
    },
    anDokumente: {
      fuehrungszeugnisAusstellung: 1,
      vorsorgeaufwendungenID: 1,
      vermoegensLeistungenID: 1,
      gesundheitszeugnisID: 1,
      fuehrungszeugnisID: 1,
      masernimpfungID: 1,
      studienbescheinigungsIDs: 1,
      lebenslaufID: 1,
      personalbogenID: 1,
      erklaerungStrafverfahrenID: 1,
      infektionsschutzID: 1,
      datenschutzID: 1,
      selbstversplichtungserklaerungID: 1,
      zeugnisseIDs: 1,
      sonstigeIDs: 1,
      arbeitsvertragIDs: 1,
      nachtragIDs: 1,
      beschaeftigungsgenehmigungIDs: 1,
    },
    anSchwerbehinderung: {
      istVorhanden: 1,
      grad: 1,
    },
    anEinverstaendnisErklaerungen: {
      selbstverpflichtung: 1,
      datenschutz: 1,
      infektionsschutz: 1,
      studentSozialversicherungspflicht: 1,
      mitarbeiterInformation: 1,
    },
    anBeschaeftigung: {
      employeeType: 1,
      personenGruppe: 1,
      abrechnungsgruppe: 1,
    },
  },
  sorgeberechtigteEltern: subPersonSelectionSet,
  sorgeberechtigteKinder: subPersonSelectionSet,
  spender: subPersonSelectionSet,
  spendenKinder: subPersonSelectionSet,
  // the person view stores only names and birth dates of the siblings - anything more would come back empty
  geschwister: { _id: 1, profilDaten: { stammdaten: { vorname: 1, nachname: 1, geburtsDatum: 1 } } },
  buchungen: bookingSelectionSet,
  activeBuchungen: bookingSelectionSet,
  bookingsOfChildren: bookingSelectionSet,
  activeBuchungenOfChildren: bookingSelectionSet,
};

export const simpleDataviewWhitelist: BaseQLSelectionSet<Person> = {
  email: 1,
  debitorennummer: 1,

  profilDaten: {
    stammdaten: {
      vorname: 1,
      nachname: 1,
    },
  },
  mitarbeiterDaten: {
    personalNr: 1,
  },
};
